import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ComicCard from '@/components/feature/ComicCard';
import SectionHeader from '@/components/feature/SectionHeader';
import JsonLd from '@/components/base/JsonLd';
import { GENRES, comics, hotComics, pickComics } from '@/mocks/comics';

const HERO_TAGS = ['古风恋爱', '现代言情', '甜宠少女', '校园青春', '重生逆袭'];

const FAQS = [
  {
    q: '桃花漫画网的恋爱漫画需要付费吗？',
    a: '桃花漫画网提供大量免费章节供读者试读，热门恋爱漫画大多设有丰富的免费章节，方便你先体验画风与剧情。部分完结作品与最新更新会提示追更状态，你可以放心挑选喜欢的作品开始阅读。',
  },
  {
    q: '如何快速找到想看的言情漫画？',
    a: '你可以在首页顶部的搜索框直接输入漫画名或关键词，也可以进入"全部漫画"页面，按古风、现代言情、甜宠、校园、都市等题材分类浏览，还能根据人气、评分和更新时间排序，快速锁定目标。',
  },
  {
    q: '漫画更新了多久会同步到网站？',
    a: '热门作品的更新会在发布后第一时间同步上线，首页"最新更新"板块会实时展示最近更新的作品与章节。你还可以通过更新时间标签，一眼掌握追更进度。',
  },
  {
    q: '古风恋爱漫画分类都有哪些类型？',
    a: '古风恋爱分类包含重生、穿越、宫斗、玄幻、仙侠、民间甜宠等多种题材，如《桃花醉》《一梦千年》《王爷的宠妃》等热门作品，既有权谋虐恋，也有轻松团宠，选择非常丰富。',
  },
];

export default function Home() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const hot = hotComics.slice(0, 8);
  const latest = useMemo(
    () => [...comics].sort((a, b) => a.id % 4 - (b.id % 4)).slice(0, 6),
    [],
  );
  const ranking = useMemo(
    () => [...comics].sort((a, b) => b.rating - a.rating).slice(0, 5),
    [],
  );
  const picks = pickComics.length ? pickComics : comics.slice(0, 3);

  const onHeroSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/category?q=${encodeURIComponent(q)}` : '/category');
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <>
      <JsonLd data={faqSchema} />

      {/* Hero */}
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Dreamy%20romantic%20peach%20blossom%20landscape%20illustration%2C%20vast%20fields%20of%20pink%20cherry%20and%20peach%20blossoms%20under%20soft%20pastel%20sky%2C%20drifting%20petals%20in%20warm%20breeze%2C%20gentle%20river%20and%20distant%20misty%20mountains%2C%20soft%20warm%20peach%20and%20cream%20color%20palette%2C%20stylized%20artistic%20anime%20background%2C%20ethereal%20soft%20light%2C%20serene%20and%20beautiful%20romance%20atmosphere&width=1600&height=900&seq=hero-home&orientation=landscape"
            alt="桃花漫画网唯美桃花背景"
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/40 via-foreground-950/30 to-background-50" />
        </div>

        <div className="relative mx-auto flex w-full max-w-7xl flex-col items-center px-4 pb-28 pt-16 text-center md:px-6 md:pb-36 md:pt-24">
          <span className="mb-5 inline-flex items-center gap-2 rounded-full bg-background-50/90 px-4 py-1.5 text-xs font-medium text-primary-600">
            <i className="ri-heart-3-fill text-primary-500" />
            桃花漫画网 · 恋爱言情漫画在线看
          </span>
          <h1 className="max-w-3xl font-display text-3xl font-bold leading-snug text-background-50 md:text-5xl">
            古风恋爱 · 现代言情
            <span className="block text-primary-200">甜宠少女漫画在线看</span>
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-7 text-background-100/90 md:text-base">
            分类齐全，热门恋爱漫画实时更新，免费章节丰富。想看哪部直接搜，入口就在首页，追更不迷路。
          </p>

          <form
            onSubmit={onHeroSearch}
            className="mt-8 flex w-full max-w-xl flex-col gap-2 sm:flex-row sm:items-center sm:rounded-full sm:border sm:border-background-50/30 sm:bg-background-50/90 sm:p-2"
          >
            <label className="flex flex-1 items-center gap-3 rounded-full bg-background-50/90 px-4 py-3 sm:bg-transparent">
              <i className="ri-search-line text-foreground-400" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入漫画名或关键词，直接搜"
                className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              />
            </label>
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              立即搜索
            </button>
          </form>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs text-background-100/80">大家都在搜：</span>
            {HERO_TAGS.map((t) => (
              <Link
                key={t}
                to={`/category?q=${encodeURIComponent(t)}`}
                className="rounded-full bg-background-50/20 px-3 py-1 text-xs text-background-50 ring-1 ring-background-50/30 transition-colors hover:bg-background-50/35"
              >
                {t}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Category chips */}
      <section className="mx-auto -mt-14 max-w-7xl px-4 md:px-6">
        <div className="flex flex-wrap items-center justify-center gap-2.5 rounded-2xl bg-background-50 px-4 py-4 shadow-soft md:gap-3">
          {GENRES.filter((g) => g !== '全部').map((g) => (
            <Link
              key={g}
              to={`/category?g=${encodeURIComponent(g)}`}
              className="rounded-full bg-background-100 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
            >
              {g}
            </Link>
          ))}
          <Link
            to="/category"
            className="whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            全部漫画
          </Link>
        </div>
      </section>

      {/* Hot ongoing */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <SectionHeader
          eyebrow="人气连载"
          title="热门恋爱漫画"
          sub="实时更新，大家都在追的恋爱好漫"
          moreTo="/category?sort=views"
          moreLabel="查看全部"
        />
        <div className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 lg:grid-cols-4">
          {hot.map((c) => (
            <ComicCard key={c.id} comic={c} />
          ))}
        </div>
      </section>

      {/* Pick featured */}
      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <SectionHeader
            eyebrow="编辑精选"
            title="本期力荐 · 甜宠少女漫画"
            sub="编辑部从海量作品里挑出的必看之作"
            moreTo="/category?sort=rating"
            moreLabel="更多精选"
          />
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            {picks.slice(0, 2).map((c, i) => (
              <Link
                key={c.id}
                to={`/comic/${c.id}`}
                className="group flex flex-col gap-5 overflow-hidden rounded-2xl bg-background-50 p-4 sm:flex-row sm:items-center sm:p-5"
              >
                <div className="relative aspect-[16/10] w-full overflow-hidden rounded-xl sm:aspect-[4/5] sm:w-48">
                  <img
                    src={c.cover}
                    alt={`${c.title}漫画`}
                    title={`${c.title}免费章节在线阅读`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-2 top-2 rounded-full bg-accent-500 px-2 py-0.5 text-[11px] font-medium text-background-50">
                    编辑力荐 {i + 1}
                  </span>
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-xl font-bold text-foreground-900 group-hover:text-primary-600 md:text-2xl">
                    {c.title}
                  </h3>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-secondary-900">{c.genre}</span>
                    {c.tags.slice(0, 2).map((t) => (
                      <span key={t} className="text-foreground-500">{t}</span>
                    ))}
                    <span className="flex items-center gap-1 text-accent-600">
                      <i className="ri-star-fill text-[11px]" />
                      {c.rating}
                    </span>
                  </div>
                  <p className="mt-3 line-clamp-3 text-sm leading-6 text-foreground-600">{c.intro}</p>
                  <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-2 text-sm font-medium text-primary-700 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                    开始追更 <i className="ri-arrow-right-line text-sm" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest + Ranking */}
      <section className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-14 md:px-6 lg:grid-cols-3 lg:py-20">
        <div className="lg:col-span-2">
          <SectionHeader eyebrow="追更不迷路" title="最新更新" sub="实时同步，第一时间追更" moreTo="/category?sort=latest" moreLabel="更多更新" />
          <ul className="divide-y divide-background-200 rounded-2xl bg-background-100/60 px-4 md:px-5">
            {latest.map((c) => (
              <li key={c.id}>
                <Link to={`/comic/${c.id}`} className="group flex items-center gap-4 py-4">
                  <span className="relative h-16 w-12 shrink-0 overflow-hidden rounded-md">
                    <img
                      src={c.cover}
                      alt={`${c.title}漫画封面`}
                      className="h-full w-full object-cover object-top"
                    />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h4 className="truncate text-sm font-bold text-foreground-800 group-hover:text-primary-600">
                      {c.title}
                    </h4>
                    <p className="mt-1 truncate text-xs text-foreground-500">
                      {c.latestChapter}
                    </p>
                  </div>
                  <span className="hidden text-xs text-foreground-400 sm:block">{c.updatedAt}</span>
                  <span className="shrink-0 whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                    免费
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <SectionHeader eyebrow="大家都在看" title="本站排行榜" sub="人气与评分俱佳" moreTo="/ranking" moreLabel="完整榜单" />
          <ol className="rounded-2xl bg-background-100/60 px-4 py-2 md:px-5">
            {ranking.map((c, i) => (
              <li key={c.id}>
                <Link to={`/comic/${c.id}`} className="group flex items-center gap-3 py-3.5">
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                      i < 3 ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-500'
                    }`}
                  >
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h4 className="truncate text-sm font-bold text-foreground-800 group-hover:text-primary-600">
                      {c.title}
                    </h4>
                    <p className="mt-0.5 text-xs text-foreground-500">{c.genre} · {c.views}</p>
                  </div>
                  <span className="flex items-center gap-1 text-xs text-accent-600">
                    <i className="ri-star-fill text-[11px]" />
                    {c.rating}
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-6">
          <SectionHeader eyebrow="常见问题" title="关于桃花漫画网" sub="快速了解我们的阅读服务" />
          <div className="space-y-3">
            {FAQS.map((f) => (
              <details
                key={f.q}
                className="group rounded-xl bg-background-50 px-5 py-4 open:bg-background-50"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-bold text-foreground-800">
                  <span>{f.q}</span>
                  <i className="ri-add-line text-lg text-primary-500 transition-transform group-open:rotate-45" />
                </summary>
                <p className="mt-3 text-sm leading-7 text-foreground-600">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20abstract%20warm%20peach%20and%20pink%20watercolor%20gradient%20background%20with%20scattered%20cherry%20blossom%20petals%20floating%2C%20gentle%20light%20bokeh%2C%20cozy%20dreamy%20romance%20atmosphere%2C%20minimalist%20elegant%20illustration%20style%2C%20calm%20and%20warm%20tones%20suitable%20for%20a%20website%20call%20to%20action%20banner&width=1600&height=700&seq=cta-home&orientation=landscape"
            alt="桃花漫画网阅读号召背景"
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/30 via-foreground-950/40 to-foreground-950/30" />
        </div>
        <div className="relative mx-auto flex w-full max-w-4xl flex-col items-center px-4 py-16 text-center md:py-24">
          <h2 className="font-display text-2xl font-bold text-background-50 md:text-4xl">
            准备好了吗？开启你的恋爱漫画之旅
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-7 text-background-100/90">
            数百部古风恋爱、现代言情、甜宠少女漫画等你来追，免费章节多，更新快，搜一搜就能找到想看的作品。
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/category"
              className="whitespace-nowrap rounded-full bg-background-50 px-7 py-3 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-100"
            >
              浏览全部漫画
            </Link>
            <Link
              to="/ranking"
              className="whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              查看排行榜
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}